/**
 * Capacity — the Loading vs Capacity surface (page shell only, CD-7).
 *
 * THE PAGE OWNS ITS SCOPE (19 Aug 2026, Mike): entity, horizon and lens, declared here
 * rather than inherited from a global bar. This surface has no year and no month/week
 * grain — a forward schedule runs from today — so those axes are simply absent instead of
 * sitting inert above the page.
 *
 * THE HORIZON IS NOW A CHOICE (31 Aug 2026, Mike). It was text, because 13 weeks was a
 * constant and a select that cannot select is a fake affordance. What made it a real axis is
 * the book: 31% of Kirkstall's served forward load falls beyond week 13, and a window that
 * cannot move reports that work as absent rather than as later. It writes `weeks` to the
 * URL, so a horizon is part of a shared link. See HORIZONS.
 *
 * Entity writes through `goToSite`, not `setSite`: leaving an entity must also leave the
 * open resource, and doing it in one write is what keeps the two URL writers from racing
 * (the regression LoadingSurface.test.tsx guards).
 */

import React from 'react'
import LoadingSurface from '../components/loading/LoadingSurface'
import ScopeBar, { type ScopeAxis } from '../components/kit/ScopeBar'
import { HORIZONS, useSurfaceState, type Horizon } from '../hooks/useLoadingSurface'
import { useScope, SITES } from '../hooks/useScope'
import type { Site } from '../types/metrics'

const LENSES = [
  { value: 'scheduled', label: 'As scheduled' },
  { value: 'levelled', label: 'Levelled' },
]

function axesFor(site: Site, state: ReturnType<typeof useSurfaceState>): ScopeAxis[] {
  return [
    {
      label: 'Entity',
      value: site,
      onChange: (value) => state.goToSite(value as Site),
      options: SITES.map((s) => ({ value: s, label: s })),
    },
    {
      label: 'Horizon',
      value: String(state.horizon),
      onChange: (value) => state.setHorizon(Number(value) as Horizon),
      options: HORIZONS.map((weeks) => ({ value: String(weeks), label: `${weeks} weeks` })),
    },
    {
      label: 'Lens',
      value: state.view,
      onChange: (v) => state.setView(v as 'levelled' | 'scheduled'),
      options: LENSES,
    },
  ]
}

export default function Capacity(): React.JSX.Element {
  const scope = useScope()
  const state = useSurfaceState()
  return (
    <div className="cx-fade cx-page py-6 pb-20">
      <header className="mb-4">
        <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[.16em]" style={{ color: 'var(--cyan)' }}>
          Capacity, Capability &amp; People · Loading vs capacity
        </div>
        <h1 className="text-[26px] font-bold tracking-[-.02em] text-ink">Capacity</h1>
        <p className="mt-1.5 text-[13.5px] text-mut">
          Where is there room, and can we take this on? Works orders backward-scheduled from sales-order
          required dates across the next {state.horizon} weeks — read-only, and not a shop-floor schedule.
        </p>
      </header>
      <ScopeBar axes={axesFor(scope.site, state)} />
      <LoadingSurface scope={scope} />
    </div>
  )
}
